
import {
  Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, OnDestroy, Output, EventEmitter
} from '@angular/core';
import { Chart, ChartConfiguration, ChartOptions, registerables, TimeUnit } from 'chart.js';
import { AppService, CoreTypes } from 'common-cloudstudio';
import moment from 'moment';
import 'chartjs-adapter-date-fns';

Chart.register(...registerables);

@Component({
  selector: 'app-xy-chart',
  templateUrl: './xy-chart.component.html',
  styleUrls: ['./xy-chart.component.scss'],
})
export class XyChartComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef<HTMLCanvasElement>;

  @Input() data: CoreTypes.XYChartWidgetData;
  @Input() height: number = 300;
  @Input() isShared: boolean = false;
  @Input() showLegend: boolean = true;
  @Input() dateFormatHistoricalWidgets: number = 0;
  
  @Input() displayTitle: string = '';
  @Input() legendPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() xAxisTitle: string = '';
  @Input() granularity: string = 'hour';


  private chart: Chart | null = null;
  private readonly colors = [
    'rgb(14, 199, 229)', 'rgb(255, 99, 132)', 'rgb(54, 162, 235)',
    'rgb(255, 205, 86)', 'rgb(153, 102, 255)', 'rgb(255, 159, 64)'
  ];

  private _datetimeFormatTooltip: string = 'd MMM HH:mm';
  private _datetimeFormatLarge: string = 'yyyy-MM-dd\'T\'HH:mm:ss';
  dateTimeFormat: string = 'dd/MM/yyyy HH:mm:ss';
  chartStatus: number = 0;
  chartData: any = null;

  constructor(
    public appService: AppService
  ) { }

  ngOnInit(): void {
    if (this.data?.Series?.length) {
      setTimeout(() => this.buildChart(), 0);
    } else {
      this.chartStatus = 2;
    }
  }

  ngOnDestroy(): void {
    if (this.chartData) {
      this.chartData = null;
    }
    if (this.chart) this.chart.destroy();
  }

  ngOnChanges(data: SimpleChanges): void {
    if (this.data && this.data?.Series?.length) {
      this.dateTimeFormat = this.data?.TimeAxisFormat ? this.data?.TimeAxisFormat : 'MM/dd HH:mm:ss';
      setTimeout(() => this.buildChart(), 0);
    }
  }

  private buildChart(): void {
    if (!this.chartCanvas) return;
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;
    if (this.chart) this.chart.destroy();
    
    if (this.data.Series.findIndex(item => item.Values?.length > 0) < 0) {
      this.chartStatus = 2;
      return;
    }
    
    this.chartData = this.mapChartData();
    const timeUnit = this.getTimeUnit();

    const config: ChartConfiguration = {
      type: 'line',
      data: this.chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: this.showLegend,
            align: this.legendPosition === 'bottom' ? 'center' : 'end',
            position: this.legendPosition,
            labels: { usePointStyle: true }
          },
          title: {
            display: !!this.displayTitle,
            text: this.displayTitle,
          },
          tooltip: {
            enabled: true,
            intersect: false,
            mode: 'nearest',
            axis:'x'
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: timeUnit,
              tooltipFormat: (this.dateTimeFormat.indexOf('h:mm') > -1) ? this.dateTimeFormat : this.dateTimeFormat + ':ss',
              displayFormats: {
                month: this.dateTimeFormat,
                day: this.dateTimeFormat,
                hour: this.dateTimeFormat,
                minute: this.dateTimeFormat,
              }
            },
            title: {
              display: this.xAxisTitle ? true : false,
              text: this.xAxisTitle
            },
            grid: { display: false, drawBorder: false },
            ticks: {
              font: { size: 11 },
              autoSkip: true,
              padding: 10,
              autoSkipPadding: 10,
              maxRotation: 0,
              minRotation: 0
            }
          },
          y: {
            grid: {
              color: 'rgba(0, 0, 0, 0.07)',
              drawBorder: false,
            },
            title: {
              display: this.data?.LeftAxis?.AxisTitle ? true : false,
              text: this.data?.LeftAxis?.AxisTitle
            },
            min: this.data.LeftAxis.MinValue,
            max: this.data.LeftAxis.MaxValue,
            beginAtZero: false,
          }
        },
      },
      plugins: [{
        id: 'custom-tooltip',
        afterDraw(chart, args, options) {
          const ctx = chart.ctx;
          const tooltip = chart.tooltip;
          const tooltipItems = tooltip.dataPoints;

          if (tooltipItems && tooltipItems.length > 0) {
            const activePoint = tooltipItems[0];
            const x = activePoint.element.tooltipPosition().x;
            const yAxis = chart.scales['y'];
            const xAxis = chart.scales['x'];

            let topY = chart.chartArea.top;
            let bottomY = chart.chartArea.bottom;
          
            // draw line
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x, yAxis.bottom);
            ctx.lineTo(x, yAxis.top);
            ctx.lineWidth = 1;
            ctx.setLineDash([5, 10]);
            ctx.strokeStyle = 'grey';
            ctx.stroke();
            ctx.restore();
          }
        },
      }]
    };

    this.chart = new Chart(ctx, config);
    this.chartStatus = 1;
  }

  private mapChartData() {
    return {
      datasets: this.data.Series.map((serie, i) => {
        const color = this.colors[i % this.colors.length];
        const data = serie.Timestamps.map((ts, index) => {
          const date = moment(ts * 1000).toISOString();
          return { x: date, y: serie.Values[index] };
        });

        return {
          label: serie.Title || `Serie ${i + 1}`,
          type: this.getChartType(serie.Type) as any,
          data,
          backgroundColor: this.isAreaChart(serie.Type) ? color.replace('rgb', 'rgba').replace(')', ', 0.4)') : color,
          borderColor: color,
          borderWidth: 2,
          fill: this.isAreaChart(serie.Type),
          tension: this.isSplineChart(serie.Type) ? 0.4 : 0,
          pointRadius: 0
        };
      })
    };
  }

  private getTimeUnit(): TimeUnit {
    const allowedUnits: TimeUnit[] = ['second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'];
    if (allowedUnits.includes(this.granularity as TimeUnit)) return this.granularity as TimeUnit;

    const isBar = this.data?.Series?.some(s => s.Type === CoreTypes.XYSeriesChartWidgetSeriesType.Bar);
    return isBar ? 'day' : 'hour';
  }

  private getChartType(type: CoreTypes.XYSeriesChartWidgetSeriesType): string {
    switch (type) {
      case CoreTypes.XYSeriesChartWidgetSeriesType.Bar: return 'bar';
      case CoreTypes.XYSeriesChartWidgetSeriesType.Area:
      case CoreTypes.XYSeriesChartWidgetSeriesType.AreaStep:
      case CoreTypes.XYSeriesChartWidgetSeriesType.Line:
      case CoreTypes.XYSeriesChartWidgetSeriesType.Step:
      case CoreTypes.XYSeriesChartWidgetSeriesType.Spline:
        return 'line';
      default: return 'line';
    }
  }

  private isAreaChart(type: CoreTypes.XYSeriesChartWidgetSeriesType): boolean {
    return type === CoreTypes.XYSeriesChartWidgetSeriesType.Area || type === CoreTypes.XYSeriesChartWidgetSeriesType.AreaStep;
  }

  private isSplineChart(type: CoreTypes.XYSeriesChartWidgetSeriesType): boolean {
    return type === CoreTypes.XYSeriesChartWidgetSeriesType.Spline;
  }
}
